/**
 * Org Chart Service
 * Handles fetching and transforming organizational chart data from Firebase
 */

import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';

// Agent level hierarchy (from lowest to highest)
const AGENT_LEVEL_HIERARCHY = {
    'TA': 0,          // Training Associate
    'A': 1,           // Associate
    'MD': 2,          // Marketing Director
    'SMD': 3          // Senior Marketing Director
};

class OrgChartService {
    constructor() {
        this.agentLevelsCache = null;
        this.usersCache = null;
        this.cacheTimestamp = null;
        this.CACHE_DURATION = 30 * 60 * 1000; // 30 minutes (increased from 5)
        this.LOCAL_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes in localStorage
    }

    getLocalCacheKey(viewType, selectedSMDId) {
        return `orgchart-cache::${viewType || 'baseshop'}::${selectedSMDId || 'none'}`;
    }

    tryReadLocalCache(viewType, selectedSMDId) {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        const key = this.getLocalCacheKey(viewType, selectedSMDId);
        try {
            const raw = window.localStorage.getItem(key);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed?.timestamp || !parsed?.data) return null;
            const isFresh = Date.now() - parsed.timestamp < this.LOCAL_CACHE_TTL_MS;
            return isFresh ? parsed.data : null;
        } catch (err) {
            console.warn('⚠️ Failed reading org chart local cache', err);
            return null;
        }
    }

    tryWriteLocalCache(viewType, selectedSMDId, data) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        const key = this.getLocalCacheKey(viewType, selectedSMDId);
        try {
            const payload = { timestamp: Date.now(), data };
            window.localStorage.setItem(key, JSON.stringify(payload));
        } catch (err) {
            console.warn('⚠️ Failed writing org chart local cache', err);
        }
    }

    /**
     * Get current authenticated user
     */
    getCurrentUser() {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('User not authenticated');
        }
        return user;
    }

    /**
     * Fetch all users from Firebase users collection
     */
    async fetchUsersFromFirebase() {
        try {
            // Check cache first
            if (this.usersCache && this.cacheTimestamp && 
                (Date.now() - this.cacheTimestamp < this.CACHE_DURATION)) {
                console.log('✅ Using cached users data');
                return this.usersCache;
            }

            console.log('🔄 Fetching users from Firebase...');
            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);
            
            const users = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                users.push({
                    id: doc.id,
                    ...data,
                    // Normalize field names
                    firstName: data.firstName || data.name?.split(' ')[0] || '',
                    lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
                    plan: data.plan,
                    level: data.level,
                    agencyCode: data.agencyCode || '',
                    recruiterId: data.recruiterId || '',
                    recruiterName: data.recruiter || '',
                    leaderName: data.leader || '',
                    smdId: data.smdId || '',
                    smdName: data.smdName || '',
                });
            });
            
            // Log newly added users for debugging
            const recentUsers = users.filter(u => {
                const createdAt = u.createdAt?.toDate?.() || u.createdAt;
                if (!createdAt) return false;
                const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
                return createdAt.getTime?.() > dayAgo || createdAt > dayAgo;
            });
            if (recentUsers.length > 0) {
                console.log(`🆕 Found ${recentUsers.length} user(s) created in last 24h:`, 
                    recentUsers.map(u => ({
                        name: `${u.firstName} ${u.lastName}`,
                        id: u.id,
                        recruiterId: u.recruiterId,
                        recruiterName: u.recruiterName
                    }))
                );
            }

            // Cache the results
            this.usersCache = users;
            this.cacheTimestamp = Date.now();

            console.log(`✅ Loaded ${users.length} users from Firebase`);
            return users;
        } catch (error) {
            console.error('Error fetching users from Firebase:', error);
            throw error;
        }
    }

    /**
     * Get agent levels information (cached)
     */
    async getAgentLevels() {
        if (this.agentLevelsCache) {
            return this.agentLevelsCache;
        }

        // Return default hierarchy
        this.agentLevelsCache = {
            agent_levels: Object.keys(AGENT_LEVEL_HIERARCHY).map(level => ({
                level,
                hierarchy: AGENT_LEVEL_HIERARCHY[level]
            }))
        };

        return this.agentLevelsCache;
    }

    /**
     * Clear cache
     */
    clearCache() {
        this.usersCache = null;
        this.cacheTimestamp = null;
        this.agentLevelsCache = null;
        
        // Also clear localStorage cache
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const keys = Object.keys(window.localStorage);
                keys.forEach(key => {
                    if (key.startsWith('orgchart-cache::')) {
                        window.localStorage.removeItem(key);
                    }
                });
                console.log('✅ Cleared all org chart caches');
            } catch (err) {
                console.warn('⚠️ Failed clearing localStorage cache', err);
            }
        }
    }

    /**
     * Clear agent levels cache (alias for backward compatibility)
     */
    clearAgentLevelsCache() {
        this.clearCache();
    }

    /**
     * Build a normalized full name for comparison
     */
    buildFullName(user) {
        const first = (user?.firstName || '').trim();
        const last = (user?.lastName || '').trim();
        const nameField = (user?.name || '').trim();
        const combined = `${first} ${last}`.trim();
        // Prefer explicit first/last, fall back to name
        return combined || nameField;
    }

    /**
     * Check if candidate reports to manager based on recruiter fields
     */
    isReportOf(candidate, manager) {
        if (!candidate || !manager) return false;

        const managerId = manager.id;
        const managerEmail = (manager.email || '').toLowerCase();
        const managerFullName = this.buildFullName(manager);
        const managerFullNameNorm = this.normalizeName(managerFullName);

        const recruiterId = candidate.recruiterId || candidate.recruiterUID || candidate.recruiterUid;
        const recruiter = (candidate.recruiter || '').trim();
        const recruiterEmail = (candidate.recruiterEmail || '').toLowerCase();

        // Debug logging for troubleshooting
        const debugMatch = candidate.id === 'roopesh777' || manager.id === 'kash777';
        if (debugMatch) {
            console.log(`🔍 Checking if ${candidate.id} reports to ${manager.id}:`, {
                candidateRecruiterId: recruiterId,
                managerIdToMatch: managerId,
                candidateRecruiter: recruiter,
                managerFullName: managerFullName,
                recruiterEmail: recruiterEmail,
                managerEmail: managerEmail
            });
        }

        // Match by stored recruiterId -> manager.id (PRIMARY method with new data structure)
        if (recruiterId && recruiterId === managerId) {
            if (debugMatch) console.log(`✅ Matched by recruiterId`);
            return true;
        }

        // Match by recruiter email
        if (recruiterEmail && recruiterEmail === managerEmail) {
            if (debugMatch) console.log(`✅ Matched by recruiter email`);
            return true;
        }
        if (recruiter && recruiter.toLowerCase() === managerEmail) {
            if (debugMatch) console.log(`✅ Matched by recruiter name vs manager email`);
            return true;
        }

        // Match by recruiter name (normalized)
        const recruiterNorm = this.normalizeName(recruiter);
        if (recruiterNorm && recruiterNorm === managerFullNameNorm) {
            if (debugMatch) console.log(`✅ Matched by normalized recruiter name`);
            return true;
        }

        // Match by first/last combo
        const managerAltName = this.normalizeName(`${manager.firstName || ''} ${manager.lastName || ''}`);
        if (recruiterNorm && managerAltName && recruiterNorm === managerAltName) {
            if (debugMatch) console.log(`✅ Matched by first/last combo`);
            return true;
        }

        if (debugMatch) console.log(`❌ No match found`);
        return false;
    }

    /**
     * Preload agent levels cache (call this early in app lifecycle)
     */
    async preloadAgentLevels() {
        try {
            await this.getAgentLevels();
            console.log('✅ Agent levels preloaded and cached');
        } catch (error) {
            console.warn('⚠️ Failed to preload agent levels:', error.message);
        }
    }

    /**
     * Fetch and transform organizational chart data
     * @param {string} viewType - 'baseshop', 'superbase', 'superteam'
     * @param {string} selectedSMDId - SMD ID for superbase/superteam views
     */
    async fetchOrgChartData(viewType = 'baseshop', selectedSMDId = null) {
        try {
            console.log('🔄 Fetching org chart data from Firebase...', { viewType, selectedSMDId });

            // Fast path: use localStorage cache for this view to speed up navigation
            const cached = this.tryReadLocalCache(viewType, selectedSMDId);
            if (cached?.users?.length) {
                console.log('✅ Using org chart local cache');
                return cached;
            }

            // Get current user info
            const currentUser = this.getCurrentUser();
            const currentUserId = currentUser.uid;

            // Fetch all users from Firebase
            const allUsers = await this.fetchUsersFromFirebase();
            
            console.log(`📊 Loaded ${allUsers.length} total users from Firebase`);

            // Find current user's data
            const currentUserData = allUsers.find(u => u.id === currentUserId || u.email === currentUser.email);
            
            if (!currentUserData) {
                throw new Error('Current user data not found in users collection');
            }

            // Precompute children relationships once for performance
            const childrenMapAll = this.buildChildrenMap(allUsers);

            // Build hierarchy based on view type
            let filteredUsers = [];
            let rootUserId = currentUserId;

            if (viewType === 'baseshop') {
                // BaseShop: Show current user and their direct downline EXCEPT SMD users
                filteredUsers = this.buildBaseShopHierarchy(allUsers, currentUserId);
                // Filter out SMD users from the result, but allow the root/current user even if SMD
                filteredUsers = filteredUsers.filter(u => u.agentLevel !== 'SMD' || u.id === rootUserId);
                rootUserId = currentUserId;
            } else if (viewType === 'superbase') {
                // SuperBase: Show SMDs whose recruiter is the logged-in user (direct SMD reports)
                filteredUsers = allUsers.filter(u => u.agentLevel === 'SMD' && u.recruiterId === currentUserId);
            } else if (viewType === 'superteam') {
                // SuperTeam: Show all SMDs hierarchically under current user, excluding direct SuperBase SMDs
                // Get all SMDs under current user
                const allSMDsUnderUser = this.findAllSMDsUnderUser(allUsers, currentUserId, childrenMapAll);
                // Get direct SMDs (SuperBase level)
                const directSMDs = allUsers.filter(u => u.agentLevel === 'SMD' && u.recruiterId === currentUserId);
                const directSMDIds = new Set(directSMDs.map(s => s.id));
                // Filter out direct SMDs from all SMDs under user
                filteredUsers = allSMDsUnderUser.filter(u => !directSMDIds.has(u.id));
            }

            // Build children map for tree structure (filtered to visible users)
            const filteredIds = new Set(filteredUsers.map(u => u.id));
            const childrenMap = {};
            Object.keys(childrenMapAll).forEach(parentId => {
                if (!filteredIds.has(parentId)) return;
                const validChildren = (childrenMapAll[parentId] || []).filter(childId => filteredIds.has(childId));
                if (validChildren.length) {
                    childrenMap[parentId] = validChildren;
                }
            });

            // Get list of SMDs for superbase/superteam views
            const smdList = allUsers
                .filter(u => u.agentLevel === 'SMD')
                .map(u => ({
                    id: u.id,
                    name: `${u.firstName} ${u.lastName}`.trim() || u.name || u.email,
                    agentLevel: u.agentLevel,
                    agencyCode: u.agencyCode
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            // Transform users to match expected format
            let transformedUsers = filteredUsers.map(user => this.transformUser(user));

            // Merge tracker flags (training, bigEvent, keyPlayer) and netLicenseAmount from trackers collection
            try {
                const trackersMap = await this.fetchTrackersMap();                
                const usersBeforeMerge = transformedUsers.filter(u => u.training || u.bigEvent || u.keyPlayer || (u.netLicenseAmount && u.netLicenseAmount > 1000)).length;
                
                transformedUsers = transformedUsers.map(u => {
                    const t = trackersMap[u.id];
                    if (!t) return u;
                    
                    const updatedUser = {
                        ...u,
                        training: !!(t.associate?.training),
                        bigEvent: !!(t.associate?.bigEvent),
                        keyPlayer: !!(t.associate?.keyPlayer),
                        netLicenseAmount: t.associate?.netLicenseAmount || 0,
                        licensed: !!(t.licensing?.licensed),
                        hasProduction: !!(t.production),
                    };
                    
                    return updatedUser;
                });
                
                const usersAfterMerge = transformedUsers.filter(u => u.training || u.bigEvent || u.keyPlayer || (u.netLicenseAmount && u.netLicenseAmount > 1000)).length;
            } catch (e) {
                console.warn('⚠️ Failed to merge trackers into org chart users:', e?.message || e);
            }

            // Add children map to first user (for compatibility with existing code)
            if (transformedUsers.length > 0) {
                transformedUsers[0]._apiIndexes = { childrenMap };
            }

            const result = {
                users: transformedUsers,
                smd_list: smdList,
                selected_smd: selectedSMDId,
                view_type: viewType,
                current_user: currentUserData,
                total_users: transformedUsers.length
            };

            console.log('✅ Org chart data prepared:', {
                totalUsers: result.users.length,
                smdCount: result.smd_list.length,
                viewType: result.view_type
            });

            // Persist lightweight cache for faster subsequent navigation
            this.tryWriteLocalCache(viewType, selectedSMDId, result);

            return result;
        } catch (error) {
            console.error('Error fetching org chart data:', error);
            throw error;
        }
    }

    /**
     * Fetch trackers collection and return a map of userId -> tracker doc
     * Only fields under associate.* are required for visual filters
     */
    async fetchTrackersMap() {
        try {
            console.log('🔍 Attempting to fetch trackers collection...', { db: !!db });
            
            const trackersRef = collection(db, 'trackers');
            console.log('✅ Got collection reference');
            
            const snapshot = await getDocs(trackersRef);
            
            const map = {};
            let trackerCount = 0;
            let trackerWithNetLicense = 0;
            
            snapshot.forEach(docSnap => {
                const data = docSnap.data() || {};
                trackerCount++;
                
                map[docSnap.id] = {
                    associate: data.associate || {},
                    licensing: data.licensing || {},
                    production: data.production || null,
                };
                
                // Count trackers with netLicenseAmount
                if (data.associate?.netLicenseAmount && data.associate.netLicenseAmount > 1000) {
                    trackerWithNetLicense++;
                }
                
            });
            return map;
        } catch (e) {
            console.error('❌ Error fetching trackers collection:', {
                error: e?.message || String(e),
                code: e?.code,
                stack: e?.stack
            });
            return {};
        }
    }

    /**
     * Find all SMDs hierarchically under a specific user
     * Traverses through all levels to find all SMD-level users
     */
    findAllSMDsUnderUser(allUsers, userId, childrenMap = null) {
        // Use provided children map when available to avoid repeated O(n^2) scans
        const adjacency = childrenMap || this.buildChildrenMap(allUsers);
        const smds = [];
        const visited = new Set();

        const traverse = (uid) => {
            if (visited.has(uid)) return;
            visited.add(uid);

            const user = allUsers.find(u => u.id === uid);
            if (!user) return;

            if (user.agentLevel === 'SMD') smds.push(user);

            const children = adjacency[uid] || [];
            children.forEach(childId => traverse(childId));
        };

        traverse(userId);
        return smds;
    }

    /**
     * Build BaseShop hierarchy (current user + downline, excluding SMDs)
     */
    buildBaseShopHierarchy(allUsers, userId) {
        const result = [];
        const visited = new Set();

        const addUserAndDownline = (uid) => {
            if (visited.has(uid)) return;
            visited.add(uid);

            const user = allUsers.find(u => u.id === uid);
            if (user) {
                result.push(user);

                // Find all users who report to this user
                const subordinates = allUsers.filter(u => this.isReportOf(u, user));
                subordinates.forEach(sub => addUserAndDownline(sub.id));
            }
        };

        addUserAndDownline(userId);
        console.log(`✅ Built BaseShop hierarchy: ${result.length} users total`);
        return result;
    }

    /**
     * Build SuperBase hierarchy (selected SMD + their team)
     */
    buildSuperBaseHierarchy(allUsers, smdId) {
        return this.buildBaseShopHierarchy(allUsers, smdId);
    }

    /**
     * Find the top-level user in the organization
     */
    findTopLevelUser(users) {
        // Find user with highest level who has no recruiter
        const topLevelUsers = users.filter(u => !u.recruiterId && !u.recruiterName);
        
        if (topLevelUsers.length === 0) {
            // If everyone has a recruiter, find the one with highest level
            return users.reduce((top, user) => {
                const topLevel = AGENT_LEVEL_HIERARCHY[top?.agentLevel] || 0;
                const userLevel = AGENT_LEVEL_HIERARCHY[user.agentLevel] || 0;
                return userLevel > topLevel ? user : top;
            }, users[0]);
        }

        // Return the top-level user with highest level
        return topLevelUsers.reduce((top, user) => {
            const topLevel = AGENT_LEVEL_HIERARCHY[top?.agentLevel] || 0;
            const userLevel = AGENT_LEVEL_HIERARCHY[user.agentLevel] || 0;
            return userLevel > topLevel ? user : top;
        }, topLevelUsers[0]);
    }

    /**
     * Build children map (userId -> array of child userIds)
     */
    buildChildrenMap(users) {
        // Optimized O(n) builder that prefers recruiterId and uses lookups for name/email fallbacks
        const childrenMap = {};
        const startTime = Date.now();

        // Fast lookups
        const emailToId = new Map();
        const nameToIds = new Map();

        users.forEach(user => {
            if (user.email) emailToId.set(user.email.toLowerCase(), user.id);

            const fullNameNorm = this.normalizeName(this.buildFullName(user));
            if (fullNameNorm) {
                if (!nameToIds.has(fullNameNorm)) nameToIds.set(fullNameNorm, []);
                nameToIds.get(fullNameNorm).push(user.id);
            }
        });

        const addChild = (parentId, childId) => {
            if (!parentId || !childId || parentId === childId) return;
            if (!childrenMap[parentId]) childrenMap[parentId] = [];
            childrenMap[parentId].push(childId);
        };

        users.forEach(user => {
            // 1) Fast path: recruiterId
            const recruiterId = user.recruiterId || user.recruiterUID || user.recruiterUid;
            if (recruiterId) {
                addChild(recruiterId, user.id);
                return;
            }

            // 2) Email match
            const recruiterEmail = (user.recruiterEmail || user.recruiter || '').toLowerCase();
            if (recruiterEmail && emailToId.has(recruiterEmail)) {
                addChild(emailToId.get(recruiterEmail), user.id);
                return;
            }

            // 3) Name match (normalized)
            const recruiterNameNorm = this.normalizeName(user.recruiterName || user.recruiter || '');
            if (recruiterNameNorm && nameToIds.has(recruiterNameNorm)) {
                nameToIds.get(recruiterNameNorm).forEach(parentId => addChild(parentId, user.id));
                return;
            }
        });

        // De-duplicate children lists
        Object.keys(childrenMap).forEach(parentId => {
            childrenMap[parentId] = Array.from(new Set(childrenMap[parentId]));
        });

        const elapsed = Date.now() - startTime;
        console.log(`⚡ Built children map in ${elapsed}ms with ${Object.keys(childrenMap).length} parent nodes`);
        return childrenMap;
    }

    /**
     * Transform user object to match expected frontend format
     */
    transformUser(user) {
        const fullName = `${user.firstName} ${user.lastName}`.trim() || user.name || user.email;

        return {
            id: user.id,
            name: fullName,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: user.phone,
            plan: user.plan,
            level: user.level,
            agentLevel: user.agentLevel,
            agencyCode: user.agencyCode,
            status: user.status || 'Active',
            // Recruiter information
            recruiter: user.recruiterName,
            recruiterId: user.recruiterId,
            // SMD information
            smdId: user.smdId,
            smdName: user.smdName,
            // Leader information
            leader: user.leaderName,
            // Profile
            profilePicture: user.photoURL || user.profilePicture || '',
            photoURL: user.photoURL || user.profilePicture || '',
            // Additional fields
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            subordinateCount: 0 // Will be calculated by frontend
        };
    }

    /**
     * Transform API response (kept for backward compatibility)
     */
    async transformApiResponse(apiResponse, viewType = 'baseshop') {
        // This method is now deprecated but kept for compatibility
        return this.fetchOrgChartData(viewType, apiResponse?.selected_smd);
    }

    /**
     * Normalize a name: lowercase, trim, collapse spaces
     */
    normalizeName(str) {
        if (!str || typeof str !== 'string') return '';
        return str.toLowerCase().trim().replace(/\s+/g, ' ');
    }

    /**
     * Get users by specific level
     */
    async getUsersByLevel(level) {
        try {
            const allUsers = await this.fetchUsersFromFirebase();
            return allUsers
                .filter(user => user.agentLevel === level)
                .map(user => this.transformUser(user));
        } catch (error) {
            console.error(`Error fetching users for level ${level}:`, error);
            throw error;
        }
    }

    /**
     * Find a specific user by ID
     */
    async findUserById(userId) {
        try {
            const allUsers = await this.fetchUsersFromFirebase();
            const user = allUsers.find(u => u.id === userId);
            return user ? this.transformUser(user) : null;
        } catch (error) {
            console.error(`Error finding user ${userId}:`, error);
            throw error;
        }
    }

    /**
     * Get hierarchy information
     */
    async getHierarchyInfo() {
        try {
            const allUsers = await this.fetchUsersFromFirebase();
            const levels = {};
            
            // Count users by level
            allUsers.forEach(user => {
                const level = user.agentLevel || 'Unknown';
                levels[level] = (levels[level] || 0) + 1;
            });

            return {
                hierarchy: AGENT_LEVEL_HIERARCHY,
                levels: levels,
                total_users: allUsers.length
            };
        } catch (error) {
            console.error('Error fetching hierarchy info:', error);
            throw error;
        }
    }
}

// Export singleton instance
export default new OrgChartService();
